import { isCroV5, rebuildCroV5Snapshot } from "./cro-v5";
import { isPeopleReadyV2, rebuildPeopleV2Snapshot } from "./people-score";
import { AGENT_READINESS_CHECKS } from "./registry";
import { buildReport } from "./score";
import { CONVERSION_RATE_CATEGORY, type ReportSnapshot } from "./types";

export type ScanPreset = "complete" | "business" | "technical" | "conversion" | "custom";
export type ScanReportName = "agent" | "engagement";
export type ScanReportKind = "aro" | "hro" | "cro" | "channels" | "agents-test" | "aeo";

export const SCAN_REPORT_KINDS: readonly ScanReportKind[] = ["aro", "hro", "cro", "channels", "agents-test", "aeo"];

export function isAroSelected(reports: readonly ScanReportKind[]): boolean {
  return reports.includes("aro");
}

export function isCroSelected(reports: readonly ScanReportKind[]): boolean {
  return reports.includes("cro") || reports.includes("hro") || reports.includes("channels");
}

export function isChannelsSelected(reports: readonly ScanReportKind[]): boolean {
  return reports.includes("channels");
}

export function isAgentTestSelected(reports: readonly ScanReportKind[]): boolean {
  return reports.includes("agents-test");
}

export function isAeoSelected(reports: readonly ScanReportKind[]): boolean {
  return reports.includes("aeo");
}

/** Canonical stored kinds: `hro` becomes `cro`. */
export function normalizeScanReports(reports: readonly ScanReportKind[]): ScanReportKind[] {
  const out: ScanReportKind[] = [];
  const seen = new Set<ScanReportKind>();
  for (const report of reports) {
    const mapped: ScanReportKind = report === "hro" ? "cro" : report;
    if (seen.has(mapped)) continue;
    seen.add(mapped);
    out.push(mapped);
  }
  return out;
}

export function parseStoredReports(value: unknown): ScanReportKind[] | null {
  if (!value || typeof value !== "object") return null;
  const reports = (value as { reports?: unknown }).reports;
  if (!Array.isArray(reports)) return null;
  const allowed = new Set<string>(SCAN_REPORT_KINDS);
  const parsed = reports.filter((item): item is ScanReportKind => typeof item === "string" && allowed.has(item));
  if (parsed.length === 0) return null;
  return normalizeScanReports(parsed);
}

export function auditsInScope(input: {
  reports?: ScanReportKind[] | null;
  agentsSelected?: boolean;
  hasWalk?: boolean;
}): { aro: boolean; cro: boolean; channels: boolean; agentsTest: boolean; aeo: boolean } {
  if (input.reports?.length) {
    return {
      aro: isAroSelected(input.reports),
      cro: isCroSelected(input.reports),
      channels: isChannelsSelected(input.reports),
      agentsTest: isAgentTestSelected(input.reports) || Boolean(input.hasWalk),
      aeo: isAeoSelected(input.reports),
    };
  }
  return {
    aro: true,
    cro: true,
    channels: true,
    agentsTest: Boolean(input.agentsSelected) || Boolean(input.hasWalk),
    aeo: false,
  };
}

export type ScanCheckOption = {
  id: string;
  title: string;
  category: string;
  report: ScanReportName;
};

export type ScanSelection = {
  preset: ScanPreset;
  includedCheckIds: string[];
};

export const EXCLUDED_CHECK_EVIDENCE = "This check was not included in this scan.";

/** Conversion audit checks. Ids match evaluateCustomerEngagement. */
export const ENGAGEMENT_SCAN_CHECKS: ScanCheckOption[] = [
  { id: "primary_headline", title: "Primary headline", category: "First impression and value proposition", report: "engagement" },
  { id: "offering_described", title: "Business offering", category: "First impression and value proposition", report: "engagement" },
  { id: "audience_or_area", title: "Audience or service area", category: "First impression and value proposition", report: "engagement" },
  { id: "services_listed", title: "Services or products", category: "First impression and value proposition", report: "engagement" },
  { id: "cta_language", title: "Call-to-action language", category: "Call to action", report: "engagement" },
  { id: "cta_destination", title: "Call-to-action destination", category: "Call to action", report: "engagement" },
  { id: "contact_or_booking", title: "Contact or booking path", category: "Call to action", report: "engagement" },
  { id: "above_fold_cta", title: "Above-the-fold call to action", category: "Call to action", report: "engagement" },
  { id: "reviews_present", title: "Reviews or testimonials", category: "Trust and social proof", report: "engagement" },
  { id: "credentials_or_team", title: "Team or credentials", category: "Trust and social proof", report: "engagement" },
  { id: "privacy_link", title: "Privacy information", category: "Trust and social proof", report: "engagement" },
  { id: "form_or_alternate", title: "Form or another contact path", category: CONVERSION_RATE_CATEGORY, report: "engagement" },
  { id: "field_count", title: "Required form fields", category: CONVERSION_RATE_CATEGORY, report: "engagement" },
  { id: "form_labels", title: "Form labels", category: CONVERSION_RATE_CATEGORY, report: "engagement" },
  { id: "form_errors", title: "Form error messages", category: CONVERSION_RATE_CATEGORY, report: "engagement" },
  { id: "viewport_meta", title: "Mobile viewport", category: "Mobile and performance", report: "engagement" },
  { id: "mobile_nav", title: "Navigation landmark", category: "Mobile and performance", report: "engagement" },
  { id: "core_web_vitals", title: "Core Web Vitals", category: "Mobile and performance", report: "engagement" },
  { id: "phone_or_hours", title: "Phone or published hours", category: CONVERSION_RATE_CATEGORY, report: "engagement" },
  { id: "live_assistance", title: "Live assistance", category: CONVERSION_RATE_CATEGORY, report: "engagement" },
  { id: "email_or_sms", title: "Email or text message", category: CONVERSION_RATE_CATEGORY, report: "engagement" },
  { id: "scheduling_or_faq", title: "Scheduling or self-service answers", category: CONVERSION_RATE_CATEGORY, report: "engagement" },
  { id: "live_channels_not_tested", title: "Live channel behavior", category: "Not tested", report: "engagement" },
];

export type ConversionCard = {
  id: string;
  title: string;
  description: string;
  benefit: string;
  tooltip: string;
  checkIds: string[];
};

/** Customize Scan cards. One switch includes every check in the category. */
export const CONVERSION_CARDS: ConversionCard[] = [
  {
    id: "first_impression",
    title: "First impression and value proposition",
    description: "See whether visitors can tell what you do, who you serve, and why you are different.",
    benefit: "Help the right visitors recognize the business immediately.",
    tooltip: "Looks at the headline, opening description, audience or service area, and service list.",
    checkIds: ["primary_headline", "offering_described", "audience_or_area", "services_listed"],
  },
  {
    id: "call_to_action",
    title: "Call to action",
    description: "See whether the next step is clear and leads to a real contact or booking path.",
    benefit: "Turn more visits into inquiries and appointment requests.",
    tooltip: "A public link is checked. Placement above the fold is not measured in a browser.",
    checkIds: ["cta_language", "cta_destination", "contact_or_booking", "above_fold_cta"],
  },
  {
    id: "trust",
    title: "Trust and social proof",
    description: "See whether reviews, credentials, and privacy information are present.",
    benefit: "Give visitors a reason to trust the business before they reach out.",
    tooltip: "Presence is recorded. The scan does not verify that reviews or credentials are authentic.",
    checkIds: ["reviews_present", "credentials_or_team", "privacy_link"],
  },
  {
    id: "mobile",
    title: "Mobile and performance",
    description: "See whether the page declares a mobile layout and a navigation landmark.",
    benefit: "Make the site easier to use on a phone.",
    tooltip: "Core Web Vitals are not measured. A missing measurement is not a failed speed test.",
    checkIds: ["viewport_meta", "mobile_nav", "core_web_vitals"],
  },
  {
    id: "engagement",
    title: CONVERSION_RATE_CATEGORY,
    description: "See whether visitors can call, chat, write, book, or send a form.",
    benefit: "Show visitors how to reach the business.",
    tooltip: "A widget on the page is recorded as present. The scan does not start a chat.",
    checkIds: [
      "form_or_alternate",
      "field_count",
      "form_labels",
      "form_errors",
      "phone_or_hours",
      "live_assistance",
      "email_or_sms",
      "scheduling_or_faq",
    ],
  },
];

const LIVE_CHANNEL_NOTE_ID = "live_channels_not_tested";

const BUSINESS_AGENT_IDS = [
  "robots_txt",
  "xml_sitemap",
  "semantic_html",
  "machine_readable_main",
  "ai_crawler_rules",
  "jsonld_organization",
  "jsonld_services",
  "jsonld_location_hours",
  "jsonld_contact",
  "jsonld_faq_policy",
] as const;

export const SCAN_CHECK_CATALOG: ScanCheckOption[] = [
  ...AGENT_READINESS_CHECKS.map((check) => ({
    id: check.id,
    title: check.title,
    category: check.category,
    report: "agent" as const,
  })),
  ...ENGAGEMENT_SCAN_CHECKS,
];

const KNOWN_IDS = new Set(SCAN_CHECK_CATALOG.map((check) => check.id));

export function agentScanCheckIds(): string[] {
  return AGENT_READINESS_CHECKS.map((check) => check.id);
}

export function engagementScanCheckIds(): string[] {
  return ENGAGEMENT_SCAN_CHECKS.map((check) => check.id);
}

export function allScanCheckIds(): string[] {
  return SCAN_CHECK_CATALOG.map((check) => check.id);
}

export function idsForPreset(preset: Exclude<ScanPreset, "custom">): string[] {
  if (preset === "technical") return agentScanCheckIds();
  if (preset === "conversion") return engagementScanCheckIds();
  if (preset === "business") return [...BUSINESS_AGENT_IDS, ...engagementScanCheckIds()];
  return allScanCheckIds();
}

/** Keep the live-behavior note whenever a conversion check is included. */
export function withLiveChannelNote(ids: string[]): string[] {
  const unique = [...new Set(ids)];
  const engagementIds = new Set(engagementScanCheckIds().filter((id) => id !== LIVE_CHANNEL_NOTE_ID));
  const hasEngagement = unique.some((id) => engagementIds.has(id));
  if (!hasEngagement) return unique.filter((id) => id !== LIVE_CHANNEL_NOTE_ID);
  if (unique.includes(LIVE_CHANNEL_NOTE_ID)) return unique;
  return [...unique, LIVE_CHANNEL_NOTE_ID];
}

export function presetLabel(preset: ScanPreset | null): string {
  if (preset === "business") return "Business Website";
  if (preset === "technical") return "Advanced Technical";
  if (preset === "conversion") return "Conversion Optimization";
  if (preset === "custom") return "Custom";
  return "Complete Scan";
}

function sameIds(left: string[], right: string[]): boolean {
  if (left.length !== right.length) return false;
  const rightSet = new Set(right);
  return left.every((id) => rightSet.has(id));
}

export function selectionForPreset(preset: Exclude<ScanPreset, "custom">): ScanSelection {
  return { preset, includedCheckIds: idsForPreset(preset) };
}

/** Homepage / wizard report toggles. Engine mapping ignores channels and agent testing. */
export function selectionForReports(reports: readonly ScanReportKind[]): ScanSelection {
  const hasAro = isAroSelected(reports);
  const hasCro = isCroSelected(reports);
  if (hasAro && hasCro) return selectionForPreset("complete");
  if (hasAro) return selectionForPreset("technical");
  if (hasCro) return selectionForPreset("conversion");
  if (isAeoSelected(reports)) return { preset: "custom", includedCheckIds: [] };
  return selectionForPreset("complete");
}

/**
 * Checks to score for a report. Conversion IDs are the legacy engagement catalog;
 * People v2 uses P-* IDs, so an HRO-only (conversion) scan must include the full
 * engagement snapshot instead of filtering by those legacy ids.
 */
export function includedIdsForReport(
  selection: ScanSelection | null,
  report: ScanReportName,
): Set<string> | null {
  if (!selection || selection.preset === "complete") return null;
  if (report === "engagement" && selection.preset === "conversion") return null;
  return new Set(selection.includedCheckIds);
}

/** Named presets use the canonical check list. Custom drops unknown ids. */
export function normalizeScanSelection(input: {
  preset: ScanPreset;
  includedCheckIds: string[];
}): ScanSelection | { error: string } {
  if (
    input.preset === "complete" ||
    input.preset === "business" ||
    input.preset === "technical" ||
    input.preset === "conversion"
  ) {
    return selectionForPreset(input.preset);
  }
  const included = withLiveChannelNote([...new Set(input.includedCheckIds.filter((id) => KNOWN_IDS.has(id)))]);
  if (included.length === 0) return { error: "Choose at least one check." };
  if (sameIds(included, allScanCheckIds())) return selectionForPreset("complete");
  if (sameIds(included, idsForPreset("business"))) return selectionForPreset("business");
  if (sameIds(included, idsForPreset("technical"))) return selectionForPreset("technical");
  if (sameIds(included, idsForPreset("conversion"))) return selectionForPreset("conversion");
  return { preset: "custom", includedCheckIds: included };
}

export function parseStoredScanConfig(value: unknown): ScanSelection | null {
  if (!value || typeof value !== "object") return null;
  const source = value as { preset?: unknown; includedCheckIds?: unknown };
  const preset = source.preset;
  if (
    preset !== "complete" &&
    preset !== "business" &&
    preset !== "technical" &&
    preset !== "conversion" &&
    preset !== "custom"
  ) {
    return null;
  }
  if (!Array.isArray(source.includedCheckIds)) return null;
  const ids = source.includedCheckIds.filter((id): id is string => typeof id === "string");
  const normalized = normalizeScanSelection({ preset, includedCheckIds: ids });
  if ("error" in normalized) return null;
  return normalized;
}

export function scanCacheKey(normalizedUrlKey: string, selection: ScanSelection | null): string {
  const rubric = "business-v1.1";
  if (!selection || selection.preset === "complete") return `${normalizedUrlKey}|${rubric}`;
  const ids = [...selection.includedCheckIds].sort().join(",");
  return `${normalizedUrlKey}|${selection.preset}:${ids}|${rubric}`;
}

/** Excluded checks stay in the report as not tested. Their weights are unchanged. */
export function applyScanSelection(
  report: ReportSnapshot,
  includedIds: Set<string> | null,
): ReportSnapshot {
  if (!includedIds) return report;
  const checks = report.checks.map((check) => {
    if (includedIds.has(check.id)) return check;
    return {
      ...check,
      status: "not_tested" as const,
      evidence: EXCLUDED_CHECK_EVIDENCE,
      fixId: undefined,
    };
  });
  if (isCroV5(report)) {
    return rebuildCroV5Snapshot(checks, report.industry);
  }
  if (isPeopleReadyV2(report)) {
    return rebuildPeopleV2Snapshot(checks, report.industry);
  }
  return buildReport(report.version, checks);
}

export function exclusionCoverage(report: ReportSnapshot): { included: number; excluded: number } {
  const excluded = report.checks.filter((check) => check.evidence === EXCLUDED_CHECK_EVIDENCE).length;
  return { included: report.checks.length - excluded, excluded };
}
